/**
 * TanStack Query hook for fetching user notifications (T055)
 *
 * Handles:
 * - Fetching user notifications from backend API
 * - Caching and auto-refresh logic
 * - Loading and error states
 * - Pagination support
 *
 * References:
 * - spec.md: FR-014 (Member notifications for role changes)
 * - spec.md: SC-008 (Notifications delivered within 5 seconds)
 * - DC-83: Phase 7 - Member Notifications
 * - API contract: GET users/me/notifications
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Notification } from '@codemerlin/shared/types/notification';
import { api, ApiError } from '@/lib/api';

/**
 * Notifications response type
 */
export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
}

/**
 * Options for fetching user notifications
 */
export interface UseUserNotificationsOptions {
  /**
   * Maximum number of notifications to return (1-100, default 20)
   */
  limit?: number;

  /**
   * Number of notifications to skip (for pagination, default 0)
   */
  offset?: number;

  /**
   * Only return unread notifications (default false)
   * Note: MVP implementation doesn't track read status, so this is ignored
   */
  unreadOnly?: boolean;

  /**
   * Whether to enable the query
   */
  enabled?: boolean;
}

/**
 * Parsed notification with Date objects
 */
export interface NotificationParsed extends Omit<Notification, 'createdAt'> {
  createdAt: Date;
}

/**
 * Return type with parsed dates
 */
export interface NotificationsData {
  notifications: NotificationParsed[];
  total: number;
  unreadCount: number;
}

/**
 * Hook to fetch notifications for the current user
 *
 * Fetches notifications from the backend API and provides loading/error states.
 * Supports pagination and auto-refresh every 10 seconds for near-real-time updates.
 *
 * @param organizationId - Organization ID to fetch notifications for
 * @param options - Optional query parameters (limit, offset, unreadOnly, enabled)
 * @returns Query result with notifications data, loading, and error states
 */
export function useUserNotifications(
  organizationId: string | null | undefined,
  options: UseUserNotificationsOptions = {}
): UseQueryResult<NotificationsData> {
  const { limit = 20, offset = 0, unreadOnly = false, enabled = true } = options;

  return useQuery({
    queryKey: ['user-notifications', organizationId, limit, offset, unreadOnly] as const,
    queryFn: async (): Promise<NotificationsData> => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      // Build query string
      const params = new URLSearchParams();
      params.append('organizationId', organizationId);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (unreadOnly) params.append('unreadOnly', 'true');

      const data = await api.get<NotificationsResponse>(
        `/users/me/notifications?${params.toString()}`
      );

      // Parse ISO date strings to Date objects
      const parsedNotifications: NotificationParsed[] = data.notifications.map(
        (notif: Notification) => ({
          ...notif,
          createdAt: new Date(notif.createdAt),
        })
      );

      return {
        notifications: parsedNotifications,
        total: data.total,
        unreadCount: data.unreadCount,
      };
    },
    enabled: !!organizationId && enabled,
    staleTime: 10000, // 10 seconds (semi-static, auto-refresh for near-real-time)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: (query) => {
      const err = query.state.error;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return false;
      }
      return 10_000;
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
